import { useState } from "react";
import { useNavigate } from "react-router";
import { Popover } from "radix-ui";
import { Bell } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/Button";
import { IconButton } from "@/components/ui/IconButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { ApiError } from "@/lib/api/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";
import type { AppNotification } from "@/features/notifications/api";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotifications,
} from "@/features/notifications/hooks";

/**
 * The panel the bell opens. The same surface as a menu (`MENU_CONTENT`), so
 * it reads as one of them, but a column with a heading that stays put and a
 * list that scrolls under it, and measured against the popover's own room
 * rather than a dropdown's.
 */
const PANEL =
  "z-50 flex w-88 max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-md " +
  "bg-surface-raised shadow-pop outline-none ring-1 ring-line " +
  "max-h-[min(30rem,var(--radix-popover-content-available-height))]";

/** A row somebody can press: the look of a menu item, at the height of two lines. */
const ROW = "flex w-full items-start gap-2.5 rounded-xs px-2.5 py-2 text-left";

/**
 * Only a path inside the application is followed. A notification is written
 * by the server, but a link is a place the page is about to go, and that is
 * worth one line of checking.
 */
function placeFor(notification: AppNotification): string | null {
  const path = notification.link_path;
  return path && path.startsWith("/") && !path.startsWith("//") ? path : null;
}

/**
 * The bell: what the system has told the person signed in, and how much of
 * it they have not read.
 *
 * The system already wrote a notification for nearly everything it does to
 * somebody, a task given out, a leave decided, a document to sign, and until
 * now there was nowhere to read them. The number is asked for once a minute;
 * the list is fetched when the bell is opened.
 *
 * It takes nothing and knows nothing about who is looking: the notifications
 * are always the reader's own, HR and employees alike, and each carries the
 * place it is about.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { session } = useAuth();
  const count = useUnreadNotifications();
  const list = useNotifications(open);
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  // The session was told the count when it was fetched, which stands in
  // until the bell's own first answer arrives.
  const unread = count.data?.unread ?? session?.unread_notifications ?? 0;
  const rows = list.data ?? [];

  function openOne(notification: AppNotification) {
    if (!notification.read_at) markRead.mutate(notification.id);
    const place = placeFor(notification);
    if (place) {
      setOpen(false);
      void navigate(place);
    }
  }

  async function readAll() {
    try {
      await markAll.mutateAsync(undefined);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Could not mark them as read.");
    }
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <span className="relative inline-flex">
        <Popover.Trigger asChild>
          <IconButton
            label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
            icon={<Bell className="size-4" />}
            size="sm"
          />
        </Popover.Trigger>
        {unread > 0 ? (
          <span
            className="pointer-events-none absolute -right-1 -top-1 grid h-4.5 min-w-4.5 place-items-center rounded-full bg-accent px-1 text-2xs font-bold leading-none tabular-nums text-white"
            aria-hidden="true"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </span>

      <Popover.Portal>
        <Popover.Content align="end" sideOffset={6} collisionPadding={8} className={PANEL}>
          <div className="flex items-center justify-between gap-3 border-b border-line-faint py-1.5 pl-3.5 pr-1.5">
            <h2 className="text-base font-semibold text-fg">Notifications</h2>
            <Button
              variant="ghost"
              size="sm"
              loading={markAll.isPending}
              disabled={unread === 0}
              onClick={() => void readAll()}
            >
              Mark all read
            </Button>
          </div>

          {list.isLoading ? (
            <div className="space-y-2 p-3" aria-hidden="true">
              <Skeleton className="h-11 w-full" />
              <Skeleton className="h-11 w-full" />
              <Skeleton className="h-11 w-full" />
            </div>
          ) : list.isError ? (
            <p className="px-4 py-8 text-center text-base text-fg-subtle">
              Your notifications could not be loaded.
            </p>
          ) : rows.length === 0 ? (
            <p className="px-4 py-8 text-center text-base text-fg-subtle">
              Nothing has been sent to you yet.
            </p>
          ) : (
            <ul className="min-h-0 flex-1 overflow-y-auto p-1">
              {rows.map((notification) => (
                <li key={notification.id}>
                  <NotificationRow notification={notification} onOpen={openOne} />
                </li>
              ))}
            </ul>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/**
 * One notification: what happened, and when.
 *
 * An unread one carries a dot and a heavier title. A row is a button while
 * pressing it does something, which is going to the place it is about or
 * marking it read; one that is read and points nowhere is only text.
 */
function NotificationRow({
  notification,
  onOpen,
}: {
  notification: AppNotification;
  onOpen: (notification: AppNotification) => void;
}) {
  const unread = notification.read_at === null;
  const content = (
    <>
      <span
        className={cn("mt-1.5 size-2 shrink-0 rounded-full", unread && "bg-accent")}
        aria-hidden="true"
      />
      <span className="min-w-0 flex-1">
        <span className={cn("block text-base text-fg", unread && "font-semibold")}>
          {unread ? <span className="sr-only">Unread: </span> : null}
          {notification.title}
        </span>
        {notification.body ? (
          <span className="mt-0.5 line-clamp-2 break-words text-xs text-fg-muted">
            {notification.body}
          </span>
        ) : null}
        <time
          dateTime={notification.created_at}
          className="mt-0.5 block text-2xs tabular-nums text-fg-subtle"
        >
          {formatDateTime(notification.created_at)}
        </time>
      </span>
    </>
  );

  if (!unread && placeFor(notification) === null) {
    return <div className={ROW}>{content}</div>;
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(notification)}
      className={cn(
        ROW,
        "cursor-pointer outline-none transition-colors hover:bg-ink-100 focus-visible:bg-ink-100",
      )}
    >
      {content}
    </button>
  );
}
